import {ApiService} from "../services/Http";
import type {AxiosInstance, AxiosResponse} from "axios";
import type {
    Attachment,
    AuditLogDetail,
    AuditLogEntry,
    ByApprovedRowResponse,
    Category,
    Cluster,
    Company,
    ConversionRate,
    Currency,
    Division,
    FirstPageKpis,
    Initiative,
    InitiativeLock,
    InitiativeKPIValue,
    InitiativeWriter,
    KPIType,
    Lever,
    LeverGroup,
    NexoIndicators,
    PipelineResponse,
    PerClustercumSumRowResponse,
    PerClusterRowResponse,
    PerCompanyLeverRowResponse,
    PerDoiRowResponse,
    SitConfiguration,
    SitConfigurationWriter,
    SitUserProfile,
    Target,
    TValuePerClusterMonthRowResponse,
    TValuePerCompanyRowResponse,
    TValuePerMonthRowResponse,
    CompanyInput,
    Country,
} from "./ApiTypes";
import {AuditAction, SitAuditModel} from "./ApiTypes";
import type {PageResponse} from "../services/ApiTypes";
import {B} from "../build";

// noinspection PointlessBooleanExpressionJS
/**
 * For debugging
 */
export const SIT_TEST: false | string = !B.APP_IS_DEV ? false : (
    // 'TEST'
    (false)
);

export default class SitApi extends ApiService {

    constructor(public http: AxiosInstance) {
        super(http);
    }

    // User
    listUsers(filters: string[][]): Promise<AxiosResponse<PageResponse<SitUserProfile>>> {
        const params = new URLSearchParams(filters);
        return this.http.get(`/users/`, {params});
    }

    // Company configuration
    listLeverGroups(): Promise<AxiosResponse<LeverGroup[]>> {
        return this.http.get(`/lever-groups/`, {params: {no_page: ''}});
    }

    listLevers(): Promise<AxiosResponse<Lever[]>> {
        return this.http.get(`/levers/`, {params: {no_page: ''}});
    }

    // Company, cluster and currency settings (SIT admins only)
    listCountries(): Promise<AxiosResponse<Country[]>> {
        return this.http.get(`/countries/`, {params: {no_page: ''}});
    }

    saveCurrency(currency: {code: string; name: string}, id?: number): Promise<AxiosResponse<Currency>> {
        return id ? this.http.patch(`/currencies/${id}/`, currency) : this.http.post(`/currencies/`, currency);
    }

    deleteCurrency(id: number): Promise<AxiosResponse<void>> {
        return this.http.delete(`/currencies/${id}/`);
    }

    saveCluster(title: string, id?: number): Promise<AxiosResponse<Cluster>> {
        return id ? this.http.patch(`/clusters/${id}/`, {title}) : this.http.post(`/clusters/`, {title});
    }

    deleteCluster(id: number): Promise<AxiosResponse<void>> {
        return this.http.delete(`/clusters/${id}/`);
    }

    saveCompany(company: CompanyInput, id?: number): Promise<AxiosResponse<Company>> {
        return id ? this.http.patch(`/companies/${id}/`, company) : this.http.post(`/companies/`, company);
    }

    deleteCompany(id: number): Promise<AxiosResponse<void>> {
        return this.http.delete(`/companies/${id}/`);
    }

    // Lever settings (SIT admins only)
    saveLeverGroup(title: string, id?: number): Promise<AxiosResponse<LeverGroup>> {
        return id ? this.http.patch(`/lever-groups/${id}/`, {title}) : this.http.post(`/lever-groups/`, {title});
    }

    deleteLeverGroup(id: number): Promise<AxiosResponse<void>> {
        return this.http.delete(`/lever-groups/${id}/`);
    }

    saveLever(lever: {title: string; description: string; group_id: number}, id?: number): Promise<AxiosResponse<Lever>> {
        return id ? this.http.patch(`/levers/${id}/`, lever) : this.http.post(`/levers/`, lever);
    }

    deleteLever(id: number): Promise<AxiosResponse<void>> {
        return this.http.delete(`/levers/${id}/`);
    }

    /** Adds the standard procurement levers that are missing. */
    addDefaultLevers(): Promise<AxiosResponse<{created: number; levers: Lever[]}>> {
        return this.http.post(`/levers/defaults/`);
    }

    listCategories(): Promise<AxiosResponse<Category[]>> {
        return this.http.get(`/categories/`, {params: {no_page: ''}});
    }

    listCompanies(): Promise<AxiosResponse<Company[]>> {
        return this.http.get(`/companies/`, {params: {no_page: ''}});
    }

    listCurrencies(): Promise<AxiosResponse<Currency[]>> {
        return this.http.get(`/currencies/`, {params: {no_page: ''}});
    }

    listConversionRates(): Promise<AxiosResponse<ConversionRate[]>> {
        return this.http.get(`/conversion-rates/`, {params: {no_page: ''}});
    }

    addConversionRate(sourceId: number, targetId: number, rate: number): Promise<AxiosResponse<ConversionRate>> {
        return this.http.post(`/conversion-rates/`, {
            source_id: sourceId, target_id: targetId, rate: rate
        });
    }

    listClusters(): Promise<AxiosResponse<Cluster[]>> {
        return this.http.get(`/clusters/`, {params: {no_page: ''}});
    }

    listDivision(): Promise<AxiosResponse<Division[]>> {
        return this.http.get(`/divisions/`, {params: {no_page: ''}});
    }

    listKpiType(): Promise<AxiosResponse<KPIType[]>> {
        return this.http.get(`/kpi-types/`, {params: {no_page: ''}});
    }

    getSitConfiguration(): Promise<AxiosResponse<SitConfiguration>> {
        return this.http.get(`/configuration/`);
    }

    updateSitConfiguration(id: number, patch: Partial<SitConfigurationWriter>): Promise<AxiosResponse<SitConfiguration>> {
        return this.http.patch(`/configuration/` + id.toString() + `/`, patch);
    }

    // Target
    listTargets(filters: string[][]): Promise<AxiosResponse<PageResponse<Target>>> {
        const params = new URLSearchParams(filters);
        return this.http.get(`/targets/`, {params});
    }

    getTarget(id: number): Promise<AxiosResponse<Target>> {
        return this.http.get(`/targets/` + id.toString() + `/`);
    }

    createTarget(instance: Partial<Target>): Promise<AxiosResponse<Target>> {
        return this.http.post(`/targets/`, instance);
    }

    updateTarget(instance: Partial<Target>): Promise<AxiosResponse<Target>> {
        return this.http.patch(`/targets/` + instance.id?.toString() + `/`, instance);
    }

    // Initiative
    listInitiatives(filters: string[][]): Promise<AxiosResponse<PageResponse<Initiative>>> {
        const params = new URLSearchParams(filters);
        return this.http.get(`/initiatives/`, {params});
    }

    listInitiativesApproval(filters: string[][]): Promise<AxiosResponse<PageResponse<Initiative>>> {
        const params = new URLSearchParams(filters);
        params.set('doi_status', '5');
        return this.http.get(`/initiatives/`, {params});
    }

    exportInitiatives(filters: string[][]): Promise<AxiosResponse<Blob>> {
        const params = new URLSearchParams(filters);
        return this.http.get(`/initiatives/export_efficiency_map/`, {params, responseType: 'blob'});
    }

    getInitiative(id: number): Promise<AxiosResponse<Initiative>> {
        return this.http.get(`/initiatives/` + id.toString() + `/`);
    }

    /** Take or refresh the lock. 409 (holder in the body) when someone else has it. */
    lockInitiative(id: number, takeOver = false): Promise<AxiosResponse<InitiativeLock>> {
        return this.http.patch(`/initiatives/` + id.toString() + `/lock/`, takeOver ? {take_over: true} : {});
    }

    /** Release the lock while the page closes: a `keepalive` request outlives the page. */
    releaseLockInitiativeOnLeave(id: number) {
        return this.http.patch(`/initiatives/` + id.toString() + `/release_lock/`, undefined,
            {adapter: 'fetch', fetchOptions: {keepalive: true}});
    }

    releaseLockInitiative(id: number): Promise<AxiosResponse<InitiativeLock>> {
        return this.http.patch(`/initiatives/` + id.toString() + `/release_lock/`);
    }

    approveInitiative(id: number, approve: boolean): Promise<AxiosResponse<Initiative>> {
        return this.http.patch(`/initiatives/` + id.toString() + `/approve/`, {approved: approve});
    }

    createInitiative(instance: Partial<InitiativeWriter>): Promise<AxiosResponse<Initiative>> {
        return this.http.post(`/initiatives/`, instance);
    }

    updateInitiative(instance: Partial<InitiativeWriter>): Promise<AxiosResponse<Initiative>> {
        return this.http.patch(`/initiatives/` + instance.id!.toString() + `/`, instance);
    }

    initiativeAttachments(id: number): Promise<AxiosResponse<Attachment[]>> {
        return this.http.get(`/initiatives/` + id.toString() + `/attachments/`);
    }

    downloadInitiativeAttachment(initiative_id: number, attachment_id: number): Promise<AxiosResponse> {
        return this.http.get(
            `/initiatives/` + initiative_id.toString() + `/attachments/` + attachment_id.toString() + `/`,
            {responseType: "blob"});
    }

    createInitiativeAttachment(initiative_id: number, file: File): Promise<AxiosResponse<never>> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('initiative', initiative_id.toString());
        return this.http.post(`/initiatives/` + initiative_id.toString() + `/attachments/`,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
    }

    deleteInitiativeAttachment(initiative_id: number, attachment_id: number): Promise<AxiosResponse<never>> {
        return this.http.delete(`/initiatives/` + initiative_id.toString() + `/attachments/` + attachment_id.toString() + `/`);
    }

    firstPageKpis(filters: string[][]): Promise<AxiosResponse<FirstPageKpis>> {
        const params = new URLSearchParams(filters);
        return this.http.get(`/initiatives/first_page_kpis/`, {params});
    }

    /** The stage-gate pipeline at the end of each day in `dates` (YYYY-MM-DD). */
    getPipeline(dates: string[], filters: string[][] = []): Promise<AxiosResponse<PipelineResponse>> {
        const params = new URLSearchParams(filters);
        params.set('dates', dates.join(','));
        return this.http.get(`/initiatives/pipeline/`, {params});
    }

    getNexoIndicators(filters: string[][] = []): Promise<AxiosResponse<NexoIndicators>> {
        const params = new URLSearchParams(filters);
        return this.http.get(`/initiatives/nexo_indicators/`, {params});
    }

    // Financial dashboard
    fpTargetPerMonth(year: number, filters: string[][]) {
        const params = new URLSearchParams(filters);
        params.set('target_year', String(year));
        return this.http.get<TValuePerMonthRowResponse[]>(`/targets/fp_target_per_month/`, {params});
    }

    fpPerCompanyTarget(year: number, filters: string[][]) {
        const params = new URLSearchParams(filters);
        params.set('target_year', String(year));
        return this.http.get<TValuePerCompanyRowResponse[]>(`/targets/fp_per_company/`, {params});
    }

    perCusterTarget(year: number, filters: string[][]) {
        const params = new URLSearchParams(filters);
        params.set('target_year', String(year));
        return this.http.get<TValuePerClusterMonthRowResponse[]>(`/targets/per_cluster/`, {params});
    }

    initiativeContributionPerDoiCumSumRows(year: number, filters: string[][]) {
        const params = new URLSearchParams(filters);
        params.set('year', String(year));
        return this.http.get<PerDoiRowResponse[]>(`/initiatives-contribution/per_doi_cumsum/`, {params});
    }


    initiativeContributionApproved(year: number, filters: string[][]) {
        const params = new URLSearchParams(filters);
        params.set('year', String(year));
        params.set('doi_status', '5');
        return this.http.get<ByApprovedRowResponse[]>(`/initiatives-contribution/by_approved/`, {params});
    }

    initiativeContributionPerCompanyLeverRows(year: number, filters: string[][]) {
        const params = new URLSearchParams(filters);
        params.set('year', String(year));
        return this.http.get<PerCompanyLeverRowResponse[]>(`/initiatives-contribution/per_company_lever/`, {params});
    }

    initiativeContributionPerClusterRows(year: number, filters: string[][]) {
        const params = new URLSearchParams(filters);
        params.set('target_year', String(year));
        return this.http.get<PerClusterRowResponse[]>(`/initiatives-contribution/per_doi_cluster/`, {params});
    }

    initiativeContributionPerClusterCumSumRows(year: number, filters: string[][]) {
        const params = new URLSearchParams(filters);
        params.set('target_year', String(year));
        return this.http.get<PerClustercumSumRowResponse[]>(`/initiatives-contribution/per_doi_cluster_cumsum/`, {params});
    }

    listInitiativesKpiValues(kpi_id: number): Promise<AxiosResponse<InitiativeKPIValue[]>> {
        return this.http.get(`/initiatives-kpi-values/`, {params: {initiative_kpi: kpi_id}});
    }

    // Audit log
    // `includeViews` defaults to false: confidential-description VIEW events are
    // recorded for every authorized read (see sit_initiative.serializers), which
    // would otherwise flood the initiative's activity log with a low-signal entry
    // per page load.
    initiativeAuditLog(initiativeId: number, includeViews = false): Promise<AxiosResponse<AuditLogEntry[]>> {
        const targetModels = [SitAuditModel.INITIATIVE, SitAuditModel.INITIATIVE_DESCRIPTIONS, SitAuditModel.INITIATIVE_FINANCIAL];
        const actions = Object.values(AuditAction)
            .filter((a): a is AuditAction => typeof a === 'number')
            .filter(a => includeViews || a !== AuditAction.VIEW);
        return this.http.get(`/audit-logs/`, {
            params: {
                object_id: initiativeId,
                target_model__in: targetModels.join(','),
                action__in: actions.join(','),
                no_page: '',
            }
        });
    }

    targetAuditLog(targetId: number): Promise<AxiosResponse<AuditLogEntry[]>> {
        return this.http.get(`/audit-logs/`, {
            params: {
                object_id: targetId,
                target_model: SitAuditModel.COMPANY_TARGET,
                no_page: '',
            }
        });
    }

    // ADMIN-only: includes `changes` (actual before/after values). Non-admins get a 403.
    auditLogDetail(id: number): Promise<AxiosResponse<AuditLogDetail>> {
        return this.http.get(`/audit-logs/` + id.toString() + `/`);
    }
}